import { formatClassTime } from "../../../utils/batchSchedule";
import { formatCurrency, formatDate } from "../../../utils/format";

/**
 * Add to batch — what the dialog shows and decides, with no React.
 *
 * The server classifies every student (vc_be utils/batchEnrolmentRules); these
 * helpers only arrange that answer for the screen. Nothing here decides who
 * may be moved.
 */

export const ACTIONS = Object.freeze({
  ADD: "add",
  ASSIGN: "assign",
  REJOIN: "rejoin",
  MOVE: "move",
  SKIP: "skip",
});

const count = (n, terms) => `${n} ${n === 1 ? terms.lower : terms.lowerPlural}`;

/** "Weekdays · 10 AM – 12 PM", from whichever timing fields the batch has. */
export function batchTiming(batch) {
  if (!batch) return "";
  const days = batch.batchType === "WEEKEND" ? "Weekends" : batch.batchType === "WEEKDAY" ? "Weekdays" : "";
  const start = formatClassTime(batch.startTime);
  const end = formatClassTime(batch.endTime);
  const hours = start && end ? `${start} – ${end}` : start || batch.time || "";
  return [days, hours].filter(Boolean).join(" · ");
}

/** "Starts 12 Oct 2026" / "Started 1 Sep 2026", or "" without a date. */
export function batchStart(batch) {
  if (!batch?.startDate) return "";
  const verb = batch.status === "Ongoing" ? "Started" : "Starts";
  return `${verb} ${formatDate(batch.startDate)}`;
}

/** One line of facts for a batch card. The branch only where there are several. */
export function batchFacts(batch, { showBranch = false } = {}) {
  return [
    batchStart(batch),
    batchTiming(batch),
    batch?.mode || "",
    showBranch && batch?.branch?.name ? batch.branch.name : "",
    batch?.instructors?.length ? batch.instructors.join(", ") : "",
  ].filter(Boolean).join(" · ");
}

export const isFull = (seats) => !!seats && seats.limit > 0 && seats.left === 0;

/** A course with exactly one batch that has room picks itself; otherwise nothing is picked. */
export function defaultBatchFor(course) {
  const open = (course?.batches || []).filter((b) => !isFull(b.seats));
  return open.length === 1 && course.batches.length === 1 ? String(open[0]._id) : "";
}

/** "18 of 30 seats left" · "Full — 30 of 30" · "12 enrolled". */
export function seatsLabel(seats) {
  if (!seats) return "";
  if (!seats.limit) return `${seats.taken} enrolled`;
  if (seats.left === 0) return `Full — ${seats.taken} of ${seats.limit}`;
  return `${seats.left} of ${seats.limit} seats left`;
}

/** The preview's rows by what will happen to them. */
export function groupPreview(students = []) {
  const groups = { add: [], assign: [], rejoin: [], move: [], skip: [] };
  for (const row of students) (groups[row.action] || groups.skip).push(row);
  return groups;
}

/**
 * Who arrives once the admin has ticked the transfers, and whether the batch
 * has room for them. The server checks again on save; this is what the
 * button and the seat line say before it is pressed.
 */
export function arrivals(preview, transferIds = new Set()) {
  const g = groupPreview(preview?.students);
  const transfers = g.move.filter((r) => transferIds.has(r.studentId)).length;
  const joining = g.add.length + g.assign.length + g.rejoin.length;
  const arriving = joining + transfers;
  const { limit = 0, taken = 0 } = preview?.seats || {};
  const after = taken + arriving;
  return {
    joining,
    transfers,
    arriving,
    after,
    fits: !limit || after <= limit,
    over: limit ? Math.max(after - limit, 0) : 0,
  };
}

/**
 * What follows "Add to batch": save at once when nobody needs a decision and
 * the batch has room, ask when someone is in another batch of the course (or
 * the seats run out), and say so when there is nothing to do.
 */
export function nextStep(preview) {
  if (groupPreview(preview?.students).move.length) return "review";
  const { arriving, fits } = arrivals(preview);
  if (!arriving) return "nothing";
  return fits ? "save" : "review";
}

export function confirmLabel({ joining, transfers }, terms) {
  if (joining && transfers) return `Add ${joining} · Transfer ${transfers}`;
  if (transfers) return `Transfer ${count(transfers, terms)}`;
  if (joining) return `Add ${count(joining, terms)}`;
  return "Nothing to add";
}

/** The line the page shows after a save, in the institute's word for students. */
export function outcomeMessage(result, terms) {
  const joined = (result?.added || 0) + (result?.placed || 0) + (result?.rejoined || 0);
  const transferred = result?.transferred || 0;
  const name = result?.batch?.name ? `“${result.batch.name}”` : "the batch";
  if (!joined && !transferred) return `Nobody was added to ${name}.`;
  if (!transferred) return `${count(joined, terms)} added to ${name}.`;
  if (!joined) return `${count(transferred, terms)} transferred to ${name}.`;
  return `${count(joined, terms)} added and ${transferred} transferred to ${name}.`;
}

/**
 * Keep the dialog open after a save only when someone was not added for a
 * reason the admin did not choose. A transfer they unticked is their answer,
 * not news.
 */
export const needsOutcomeView = (result) => (result?.skipped || []).some((s) => !s.declinedMove);

/** The fee field's hint: what blank means, and who it does not touch. */
export function feeHint(courseFee) {
  const fee = Number(courseFee) || 0;
  const blank = fee > 0 ? `Leave blank for the course fee (${formatCurrency(fee)}).` : "Leave blank for no fee.";
  return `${blank} Only new enrolments get it — a transfer keeps its fee and payments.`;
}

/** "at ₹12,000" / "with no fee" — what new enrolments will be charged. */
export function feeNote(feeValue, courseFee) {
  const fee = feeValue !== undefined ? feeValue : Number(courseFee) || 0;
  return fee > 0 ? `at ${formatCurrency(fee)}` : "with no fee";
}

/**
 * The fee to send: blank stays blank (the server uses the course fee), else a
 * number. Returns `{ error }` for something that is not an amount.
 */
export function readFee(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return { value: undefined };
  const n = Number(text.replace(/[,\s₹]/g, ""));
  if (!Number.isFinite(n) || n < 0) return { error: "Enter an amount of 0 or more, or leave it blank." };
  return { value: n };
}
